import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../api/client';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const savedOrg = localStorage.getItem('org');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      if (savedOrg) {
        try { setOrg(JSON.parse(savedOrg)); } catch {}
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password, captcha_token) => {
    const response = await api.post('/api/auth/login', { email, password, captcha_token });
    const { access_token, refresh_token, user: userData, org: orgData } = response.data;
    
    localStorage.setItem('token', access_token);
    if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(userData));
    if (orgData) {
      localStorage.setItem('org', JSON.stringify(orgData));
      setOrg(orgData);
    }
    setUser(userData);
    
    return userData;
  };

  const setAuthFromGoogle = (accessToken, userData, orgData) => {
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
    if (orgData) {
      localStorage.setItem('org', JSON.stringify(orgData));
      setOrg(orgData);
    }
    setUser(userData);
  };

  const register = async (userData) => {
    const response = await api.post('/api/auth/register', userData);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('org');
    setUser(null);
    setOrg(null);
    window.location.href = '/login';
  };

  const refreshOrg = async () => {
    try {
      const response = await api.get('/api/orgs/current');
      if (response.data.org) {
        setOrg(response.data.org);
        localStorage.setItem('org', JSON.stringify(response.data.org));
      }
    } catch {}
  };

  const switchOrg = async (orgId) => {
    try {
      await api.post(`/api/orgs/switch/${orgId}`);
      // Re-login to get new JWT with updated org_id
      const meResp = await api.get('/api/auth/me');
      const updatedUser = meResp.data;
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      await refreshOrg();
      // Force page reload to refresh all data
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch org:', err);
    }
  };

  const updateUser = (updatedUser) => {
    setUser(prev => ({ ...prev, ...updatedUser }));
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    localStorage.setItem('user', JSON.stringify({ ...stored, ...updatedUser }));
  };

  const value = {
    user,
    org,
    loading,
    login,
    setAuthFromGoogle,
    register,
    logout,
    refreshOrg,
    switchOrg,
    updateUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isManager: user?.role === 'manager' || user?.role === 'admin',
    isDeveloper: user?.role === 'developer',
    isClientUser: user?.role === 'client' || !!user?.is_client_user,
    clientId: user?.client_id || null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
